import {useEffect, useMemo, useState} from 'react';
import {GetIssuePointCabinetResponsePayload} from '../../../../../shared/models/api/getIssuePointCabinetResponsePayload';
import {httpService, HttpWrapperOptions} from '../../../../../shared/httpWrapper';
import {getApiRoute} from '../../../../../shared/pipes';
import {apiUrls} from '../../../../../shared/constants/urls';
import {GenericPointFeature, LngLat} from '@yandex/ymaps3-types';

export const useMap = () => {
  const [loading, setLoading] = useState(true);
  const [mapData, setMapData] = useState<GetIssuePointCabinetResponsePayload[]>([]);
  const [mapDataRaw, setMapDataRaw] = useState<GetIssuePointCabinetResponsePayload[]>([]);
  const [currentMarker, setCurrentMarker] = useState<GetIssuePointCabinetResponsePayload | null>(null);
  const [markers, setMarkers] = useState<GenericPointFeature<LngLat>[]>([]);

  const centerPoint: [number, number] = useMemo(() => {
    if (!mapData.length) return [0, 0];

    const locatedPoints = mapData.filter((location) => location.geolocation?.length === 2);
    const centroid: [number, number] = locatedPoints.reduce(
      (acc, {geolocation}) => {
        if (geolocation) {
          acc[0] += geolocation[0];
          acc[1] += geolocation[1];
        }
        return acc;
      },
      [0, 0],
    );

    return [centroid[0] / locatedPoints.length, centroid[1] / locatedPoints.length] as [number, number];
  }, [mapData.length]);

  const onPressMarker = (marker: GetIssuePointCabinetResponsePayload | null) => {
    setCurrentMarker(marker);
  };

  const changeSameGeolocation = (backEndData: GetIssuePointCabinetResponsePayload[]) => {
    const issuePointsWithCoord = backEndData.filter((x) => x.geolocation?.length === 2);

    const mapWithUniqueCoords = new Map<string, GetIssuePointCabinetResponsePayload>();

    issuePointsWithCoord.forEach((issuePoint) => {
      let key = JSON.stringify(issuePoint.geolocation);
      if (mapWithUniqueCoords.has(key)) {
        issuePoint.geolocation![0] = issuePoint.geolocation![0] + 0.000025;
        issuePoint.geolocation![1] = issuePoint.geolocation![1] + 0.000025;
        key = JSON.stringify(issuePoint.geolocation);
        mapWithUniqueCoords.set(key, issuePoint);
      } else {
        mapWithUniqueCoords.set(key, issuePoint);
      }
    });

    return Array.from(mapWithUniqueCoords.values());
  };

  useEffect(() => {
    httpService
      .get<GetIssuePointCabinetResponsePayload[]>(
        getApiRoute(apiUrls.esiaIssuePoints),
        new HttpWrapperOptions().Errors(true).ThrowException(true),
      )
      .then((data) => {
        setMapDataRaw(data);
        const pointsToShow = data.filter(
          (x) => x.geolocation?.length === 2 && x.availableCardsCount && x.availableCardsCount > 0,
        );
        setMapData(pointsToShow);
        const noSameGeoLocation = changeSameGeolocation(pointsToShow);
        if (noSameGeoLocation.length)
          setMarkers(
            noSameGeoLocation.map((x) => ({
              type: 'Feature',
              id: String(x.id),
              geometry: {
                type: 'Point',
                coordinates: x.geolocation as LngLat,
              },
              availableCards: x.availableCardsCount,
            })),
          );
      })
      .catch(() => {
        setMarkers([]);
        setMapData([]);
      })
      .finally(() => setLoading(false));
  }, []);

  return {
    loading,
    mapData,
    mapDataRaw,
    centerPoint,
    currentMarker,
    onPressMarker,
    markers,
  };
};
